import {computed, hasInjectionContext, inject, provide, ref} from 'vue';
import {useNuxtApp, useRoute} from '#app';
import {onBeforeRouteLeave, onBeforeRouteUpdate} from 'vue-router';

// The visitor preview (?viewAs=visitor, the admin dashboard's "Public page" tile; X73, Grant 2026-10-20): while a
// page is previewing as a visitor, EVERY request the page and its children make carries `X-View-As: visitor`, and
// the API serves it signed out (Helpers.VisitorPreview: the token is never read, so page/club, the availability
// card, the recent results, and whatever a child component asks for all answer as they would to a stranger), and
// the components that gate on a sign-in read the same flag, so the page IS a visitor's. Before this only page/club
// sent the header, by hand, so on a club open to visitors the preview showed the admin's copies of the rest ("You"
// on their own booking cells, results a visitor may not see, Edit result on the rows).
//
// One mechanism, no per-fetch edits. The flag is one ref, kept on the nuxtApp (one per request on the server, one
// for the session in the browser; never module scope, see the ssr-crud shared-auth leak X37) and PROVIDED to the
// page's component tree by useVisitorPreviewPage:
// - the Crud headers generator (useAuthService) reads the app's ref as each useCrudApi request goes out, which
//   covers page/club and crud-list-loader (a module component that takes no headers);
// - useHttpRequest reads the INJECTED ref, so every `endpoints` call made from inside the page's tree (the
//   availability card, a child's composable) carries the header, while the app's own chrome outside the tree (the
//   layout's messaging sync, the sign-in state) stays the admin's and never gets a 401 for a call it must make;
// - a component that shows signed-in-only controls (Edit result, Message, Book) reads useVisitorPreview() and
//   treats the viewer as signed out while it is on.
// The route guards turn the flag off BEFORE the next page's setup runs (the old page is still mounted while the
// new one fetches), so a link out of the preview never carries the header.
export const VIEW_AS_HEADER = 'X-View-As';
export const VIEW_AS_VISITOR = 'visitor';
export const VISITOR_PREVIEW_KEY = Symbol('visitorPreview');
const FLAG_KEY = '_visitorPreview';
/** Outside a previewing page's tree: never on. A constant, never written. */
const OFF = computed(() => false);

/** The app's flag, made on first use: true while a page previewing as a visitor is on (read by the Crud headers generator). */
export function visitorPreviewFlag(nuxtApp) {
    return nuxtApp[FLAG_KEY] ??= ref(false);
}

/**
 * The flag of the page tree the caller is in: the previewing page's own ref, or a constant false outside one (a
 * layout, a plugin, a composable built at app level). A composable that makes requests or gates on a sign-in reads
 * it in setup and `.value` when it acts.
 */
export function useVisitorPreview() {
    return hasInjectionContext() ? inject(VISITOR_PREVIEW_KEY, OFF) : OFF;
}

/** The headers a request going out now must add: `{X-View-As: visitor}` while the preview is on, nothing otherwise. */
export function visitorPreviewHeaders(flag) {
    return flag?.value ? {[VIEW_AS_HEADER]: VIEW_AS_VISITOR} : {};
}

/**
 * Called in the setup of a page that can be previewed as a visitor, with `isOn(route)` saying whether a route is
 * the preview. Sets the app's flag for this page's route and provides it to the page's tree, follows a change of
 * query on the same page (a ?show= dialog keeps it; "Back to my view" clears it, before the remounted page's setup
 * reads its own route), and clears it before any other page is entered. No unmount hook: on a keyed remount the old
 * instance unmounts AFTER the new one's setup set the flag, and clearing it there would undo that.
 */
export function useVisitorPreviewPage(isOn) {
    const flag = visitorPreviewFlag(useNuxtApp());
    flag.value = !!isOn(useRoute());
    provide(VISITOR_PREVIEW_KEY, flag);
    onBeforeRouteUpdate((to) => { flag.value = !!isOn(to); });
    onBeforeRouteLeave(() => { flag.value = false; });
    return flag;
}
